import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { info } from 'octicons';
import { LocalStorageService, ObjetsService } from '../../shared';

@Component({
  selector: 'app-form-a',
  templateUrl: './app-form-a.component.html',
  providers: [ObjetsService]
})

export class AppFormAComponent implements OnInit {
  public static TYPE_PERSONNE_MORALE = 'pm';
  public static TYPE_PERSONNE_PHYSIQUE = 'pp';

  public formName = 'rubrique_A_proprietaire';
  public datasProf: any = [];
  public metaData: any = [];
  public hiddenPersonPp: any = [];
  public showPersonLinePp: any = [];
  public hiddenPersonPm: any = [];
  public showPersonLinePm: any = [];
  public formTooltip: any = [];
  public personneValid = false;
  public errorMessagePm = true;
  public errorMessagePp = true;
  public infoIcon: SafeHtml;
  public alive = true;

  /**
   * Émis lorsque la liste des personnes change (ajout, suppression, modification)
   */
  @Output() personsChanged = new EventEmitter<void>();

  public listeCommunes: Map<string, string[]>;
  public listLieuxDitsCp = new Map;

  private lineIndexPp = 0;
  private lineIndexPm = 0;

  country: any = [];
  personnePhysique: FormGroup;
  personneMorale: FormGroup;
  itemsPp: FormArray;
  itemsPm: FormArray;

  constructor(
    private _objetsService: ObjetsService,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private localStorageService: LocalStorageService
  ) {
    this.personnePhysique = new FormGroup({
      nom: new FormControl('', Validators.required),
      prenom: new FormControl('', Validators.required),
      nom_usage: new FormControl(),
      profession: new FormGroup({
        code: new FormControl(),
        lib: new FormControl()
      }),
      adresse_boite_aux_lettres: new FormControl(),
      adresse_entree: new FormControl(),
      adresse_numero: new FormControl(),
      adresse_nom_voie: new FormControl(),
      adresse_extension: new FormControl(),
      adresse_type_voie: new FormControl(),
      adresse_lieu_dit_bp: new FormControl(),
      adresse_bp: new FormControl(),
      adresse_cp: new FormControl('', Validators.required),
      adresse_localite: new FormControl('', Validators.required),
      adresse_cedex: new FormControl(),
      adresse_state: new FormControl(),
      adresse_indivision: new FormControl('', [Validators.min(0), Validators.max(99)]),
      adresse_country: new FormControl('', Validators.required),
      adresse_code_origine: new FormControl(''),
      adresse_telephone: new FormControl(),
      adresse_indicatif: new FormControl(),
      adresse_email: new FormControl('')
    }, {updateOn: 'submit'});

    this.personneMorale = new FormGroup({
      denomination: new FormControl('', Validators.required),
      forme_juridique: new FormControl('', Validators.required),
      siret: new FormControl('', Validators.pattern(/^$|^\d{14}$/)),
      nom: new FormControl('', Validators.required),
      prenom: new FormControl('', Validators.required),
      adresse_boite_aux_lettres: new FormControl(),
      adresse_entree: new FormControl(),
      adresse_numero: new FormControl(),
      adresse_extension: new FormControl(),
      adresse_nom_voie: new FormControl(),
      adresse_type_voie: new FormControl(),
      adresse_lieu_dit_bp: new FormControl(),
      adresse_bp: new FormControl(),
      adresse_cp: new FormControl('', Validators.required),
      adresse_localite: new FormControl('', Validators.required),
      adresse_cedex: new FormControl(),
      adresse_state: new FormControl(),
      adresse_indivision: new FormControl('', [Validators.min(0), Validators.max(99)]),
      adresse_country: new FormControl('', Validators.required),
      adresse_code_origine: new FormControl(''),
      adresse_telephone: new FormControl(),
      adresse_indicatif: new FormControl(),
      adresse_email: new FormControl('')
    }, {updateOn: 'submit'});

    this.resetPersonnePhysique();
    this.resetPersonneMorale();
  }

  /**
   * RAZ de la liste personne morale
   */
  resetPersonnePhysique(): void {
    this.lineIndexPp = 0;
    this.hiddenPersonPp = [];
    this.showPersonLinePp = [];
    this.showPersonLinePp[0] = true;
    this.hiddenPersonPp[0] = true;

    this.personnePhysique = this.formBuilder.group({
      itemsPp: this.formBuilder.array([ this.createItem(AppFormAComponent.TYPE_PERSONNE_PHYSIQUE) ])
    });
  }

  /**
   * RAZ de la liste personne morale
   */
  resetPersonneMorale(): void {
    this.lineIndexPm = 0;
    this.hiddenPersonPm = [];
    this.showPersonLinePm = [];
    this.showPersonLinePm[0] = true;
    this.hiddenPersonPm[0] = true;

    this.personneMorale = this.formBuilder.group({
      itemsPm: this.formBuilder.array([ this.createItem(AppFormAComponent.TYPE_PERSONNE_MORALE) ])
    });
  }

  /**
   * Le tableau des PM
   */
  get personneMoraleData(): FormArray {
    return this.personneMorale.get('itemsPm') as FormArray;
  }

  /**
   * Le tableau des PP
   */
  get personnePhysiqueData(): FormArray {
    return this.personnePhysique.get('itemsPp') as FormArray;
  }

  ngOnInit (): void {
    this.country = Object.keys(this._objetsService.getCountry()).map(key => ({type: key, value: this._objetsService.getCountry()[key]}));
    this.infoIcon = this.sanitizer.bypassSecurityTrustHtml(info.toSVG());
    this.getTooltip();

    this.showPersonLinePp[0] = true;
    this.showPersonLinePm[0] = true;

    this.hiddenPersonPp[0] = true;
    this.hiddenPersonPm[0] = true;

    this.restorePersonsFromStorage();

    // Auto-sauvegarder le brouillon du formulaire pendant la saisie
    this.personnePhysique.valueChanges.subscribe(() => this.savePersonsToStorage());
    this.personneMorale.valueChanges.subscribe(() => this.savePersonsToStorage());
  }

  /**
   * Créée un nouvel élément programmatiquement (sans vérification préalable)
   *
   * @param i index (démarre à 0)
   * @param type type de personne (pp/pm)
   * @returns le groupe correspondant
   */
  addNewItem(i, type): FormGroup {
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE) {
      this.lineIndexPp = this.lineIndexPp + 1;
      this.showPersonLinePp[this.lineIndexPp] = true;

      this.itemsPp = this.personnePhysiqueData;
      const group = this.createItem(AppFormAComponent.TYPE_PERSONNE_PHYSIQUE);
      this.itemsPp.push(group);

      this.hiddenPersonPp[i] = true;
      this.showPersonLinePp[i] = false;
      this.hiddenPersonPp[this.lineIndexPp] = true;

      return group;

    } else if (type === AppFormAComponent.TYPE_PERSONNE_MORALE) {
      this.lineIndexPm = this.lineIndexPm + 1;
      this.showPersonLinePm[this.lineIndexPm] = true;

      this.itemsPm = this.personneMoraleData;
      const group = this.createItem(AppFormAComponent.TYPE_PERSONNE_MORALE);
      this.itemsPm.push(group);

      this.hiddenPersonPm[i] = true;
      this.showPersonLinePm[i] = false;
      this.hiddenPersonPm[this.lineIndexPm] = true;
      return group;
    } else {
      // Normalement pas possible
      return null;
    }
  }

  /**
   * Ajoute une personne
   *
   * @param i index (démarre à 0)
   * @param type type de personne (pp/pm)
   * @returns le groupe correspondant
   */
  addItem(i, type): FormGroup {
    if (this.personneValid === true) {
      const result = this.addNewItem(i, type);
      this.personneValid = false;
      this.savePersonsToStorage();
      this.personsChanged.emit();
      return result;
    }

    return null;
  }

  /**
   * Effectue la préselection de la select box
   * 'profession' lors de l'affichage d'une personne existante
   * @param index index de la personne en cours
   */
  public preloadPersonProfessionSelectBox() {
    const professionValue = this.personnePhysique.get('itemsPp').get(this.lineIndexPp.toString()).get('profession').value;

    let professionIndex = 0;
    if(professionValue != null) {
      for(const profession of this.datasProf) {
        professionIndex++;
        if(profession.cspCode === professionValue.code) {
          break;
        }
      }
    }

    // Forcer la selection de la profession (si l'élément DOM existe)
    const selectElement = document.getElementById(`personne-physique-profession-${this.lineIndexPp}`) as HTMLSelectElement;
    if (selectElement != null) {
      selectElement.selectedIndex = professionIndex;
    }
  }

  /**
   * Affiche le formulaire d'une personne quand on clique sur une ligne de la liste
   *
   * @param i
   * @param type
   */
  showPerson(i, type): void {
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE)  {
      if (this.hiddenPersonPp[i]) {
        this.hiddenPersonPp[i] = false;
      } else {
        this.hiddenPersonPp[i] = true;
        this.savePersonsToStorage();
        this.personsChanged.emit();
      }
    } else if (type === AppFormAComponent.TYPE_PERSONNE_MORALE) {
      if (this.hiddenPersonPm[i]) {
        this.hiddenPersonPm[i] = false;
      } else {
        this.hiddenPersonPm[i] = true;
        this.savePersonsToStorage();
        this.personsChanged.emit();
      }
    } else {
      // Normalement impossible
    }
  }

  /**
   * Valide le formulaire de la personne morale avant de replier la ligne.
   * Si le formulaire est invalide, affiche les erreurs et ne replie pas.
   */
  validateAndSavePerson(i, type): void {
    const typePerson = type === AppFormAComponent.TYPE_PERSONNE_MORALE ? 'morale' : 'physique';
    this.checkPersonneValidity(typePerson);
    if (type === AppFormAComponent.TYPE_PERSONNE_MORALE && !this.personneMorale.valid) {
      return;
    }
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE && !this.personnePhysique.valid) {
      return;
    }
    this.showPerson(i, type);
  }

  /**
   * Supprime une personne physique ou morale de la liste des personne grâce au bouton "Supprimer"
   *
   * @param i
   * @param type
   */
  deletePerson(i, type): void {
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE)  {
      this.hiddenPersonPp.splice(i, 1);
      this.itemsPp.removeAt(i);
      this.lineIndexPp = this.lineIndexPp - 1;
      this.showPersonLinePp[this.lineIndexPp] = true;
    } else if (type === AppFormAComponent.TYPE_PERSONNE_MORALE) {
      this.hiddenPersonPm.splice(i, 1);
      this.itemsPm.removeAt(i);
      this.lineIndexPm = this.lineIndexPm - 1;
      this.showPersonLinePm[this.lineIndexPm] = true;
    } else {
      // Normalement impossible
    }
    this.savePersonsToStorage();
    this.personsChanged.emit();
  }

  /**
   * Pilote l'affichage du formulaire d'ajout d'une personne physique ou morale
   *
   * @param type
   */
  getAddForm(type): void {
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE)  {
      if (this.hiddenPersonPp[this.lineIndexPp] === false) {
        this.hiddenPersonPp[this.lineIndexPp] = true;
      } else {
        this.hiddenPersonPp[this.lineIndexPp] = false;
      }
      if (this.hiddenPersonPm[this.lineIndexPm] === false) {
        this.hiddenPersonPm[this.lineIndexPm] = true;
      }
    } else if (type === AppFormAComponent.TYPE_PERSONNE_MORALE) {
      if (this.hiddenPersonPm[this.lineIndexPp] === false) {
        this.hiddenPersonPm[this.lineIndexPm] = true;
      } else {
        this.hiddenPersonPm[this.lineIndexPm] = false;
      }
      if (this.hiddenPersonPp[this.lineIndexPp] === false) {
        this.hiddenPersonPp[this.lineIndexPp] = true;
      }
    } else {
      // Normalement impossible
    }
    this.savePersonsToStorage();
  }

  public getListLieuxDit(i: number, type: string): void {
    let selectedCp: number;
    const mapKey = type + '_' + i.toString();
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE) {
      selectedCp = this.personnePhysiqueData.get(i.toString()).get('adresse_cp').value;
    }
    if (type === AppFormAComponent.TYPE_PERSONNE_MORALE) {
      selectedCp = this.personneMoraleData.get(i.toString()).get('adresse_cp').value;
    } else {
      // Normalement impossible
    }

    if (selectedCp.toString().substring(0, 2) === '34') {
      this.listLieuxDitsCp.set(mapKey, this.listeCommunes.get(selectedCp.toString()));
    } else {
      this.listLieuxDitsCp.set(mapKey, undefined);
    }
  }

  /**
   * Créé le formgroup pour le formarray
   *
   * @param type
   * @returns {FormGroup}
   */
  createItem(type): FormGroup {
    if (type === AppFormAComponent.TYPE_PERSONNE_PHYSIQUE)  {
      const group = this.formBuilder.group({
        nom: '',
        prenom: '',
        nom_usage: '',
        profession: null,
        adresse_boite_aux_lettres: '',
        adresse_entree: '',
        adresse_numero: '',
        adresse_extension: '',
        adresse_nom_voie: '',
        adresse_type_voie: '',
        adresse_lieu_dit_bp: '',
        adresse_bp: '',
        adresse_cp: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
        adresse_localite: '',
        adresse_cedex: ['', [Validators.min(1), Validators.max(99)]],
        adresse_state: '',
        adresse_indivision: ['', [Validators.min(1), Validators.max(99)]],
        adresse_country: 'FR',
        adresse_code_origine: '',
        adresse_telephone: '',
        adresse_indicatif: '',
        adresse_email: ''
      });
      this.setupCpLogic(group, 'adresse_country', 'adresse_cp');
      return group;
    } else if (type === AppFormAComponent.TYPE_PERSONNE_MORALE) {
      const group = this.formBuilder.group({
        denomination: '',
        forme_juridique: '',
        siret: ['', [Validators.required, Validators.pattern(/^\d{14}$/)]],
        nom: '',
        prenom: '',
        adresse_boite_aux_lettres: '',
        adresse_entree: '',
        adresse_numero: '',
        adresse_extension: '',
        adresse_nom_voie: '',
        adresse_type_voie: '',
        adresse_lieu_dit_bp: '',
        adresse_bp: '',
        adresse_cp: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
        adresse_localite: '',
        adresse_cedex: ['', [Validators.min(1), Validators.max(99)]],
        adresse_state: '',
        adresse_indivision: ['', [Validators.min(1), Validators.max(99)]],
        adresse_country: 'FR',
        adresse_code_origine: '',
        adresse_telephone: '',
        adresse_indicatif: '',
        adresse_email: ''
      });
      this.setupCpLogic(group, 'adresse_country', 'adresse_cp');
      return group;
    } else {
      // Normalement impossible
    }
  }

  /**
   * Function mettant à jour les variables pilotant les messages d'erreur en cas d'invalidité du formulaire de personnePhysique ou morale,
   * et ceci au moment ou l'utilisatuer appuie sur le bouton Enregistrer
   *
   * @param type
   */
  checkPersonneValidity(type): void {
    let valid;

    if (type === 'physique') {
      valid = this.personnePhysique.valid;
    } else if (type === 'morale') {
      valid = this.personneMorale.valid;
    }

    if (valid) {
      this.personneValid = true;
      if (type === 'physique') {
        this.errorMessagePp = true;
      } else if (type === 'morale') {
        this.errorMessagePm = true;
      }
    } else {

      if (type === 'physique') {
        this.errorMessagePp = false;
      } else if (type === 'morale') {
        this.errorMessagePm = false;
      }
    }
  }

  /**
   * Fonction gérant l'affichage des messages de non validités des champs ainsi que la classe associée à cette dernière
   *
   * @param dirty
   * @param typePerson
   * @param {string} type
   * @returns {any}
   */
  checkFieldValidity(dirty, typePerson, type = 'validity'): any {
    const valid = this.showErrorMessage(typePerson);

    switch (type) {
      case 'class':
        if (!valid && !dirty) {
          return 'is-invalid';
        } else {
          return '';
        }
      default:
        if (!valid && !dirty) {
          return true;
        } else {
          return false;
        }
    }
  }

  /**
   * Renvoit un boolean en fonction de la validité des formulaires présent dans l'étape courante
   *
   * @param type
   * @returns {boolean}
   */
  showErrorMessage(type): boolean {
    if (type === 'morale' && this.personneValid === false) {
      return this.errorMessagePm;
    } else if(type === 'physique' && this.personneValid === false) {
      return this.errorMessagePp;
    } else {
      return true;
    }
  }

  /**
   * Met à jour la variable formTooltip qui est utilisé pour mettre à jour le contenu des tooltips
   */
  getTooltip(): void {
    this.formTooltip = [
      {text:'Le siège à indiquer dans cette rubrique est celui du principal établissement ;' +
        'lorsque la déclaration ou la demande est présentée par un établissement secondaire, ' +
        'le signataire devra en indiquer l’adresse à la rubrique H et remplir également la rubrique A.'},
      {text:'N\'est soumise à déclaration d\'intention d\'aliéner que la cession de l’ensemble ' +
        'des droits indivis représentant l\'immeuble dans son entier, sauf pour le droit de préemption ' +
        'du Conservatoire de l’espace littoral et des rivages lacustres qui est applicable à la cession' +
        ' de droits indivis sur un immeuble ou une partie d\'immeuble bâti ou non bâti ainsi qu\'à la ' +
        'cession de la majorité des parts d\'une société civile immobilière lorsque le patrimoine de cette' +
        ' société est constitué par une unité foncière, bâtie ou non, sur la totalité ou certaines parties' +
        ' des zones de préemption créées par le conseil départemental ou le conservatoire.'},
    ];
  }

  /**
   * Sauvegarde les personnes physiques et morales enregistrées dans le localStorage.
   * Exclut la dernière entrée vide (formulaire d'ajout).
   */
  private savePersonsToStorage(): void {
    const ppControls = this.personnePhysiqueData.controls;
    const ppValues = [];
    for (let i = 0; i < ppControls.length - 1; i++) {
      ppValues.push(ppControls[i].value);
    }

    const pmControls = this.personneMoraleData.controls;
    const pmValues = [];
    for (let i = 0; i < pmControls.length - 1; i++) {
      pmValues.push(pmControls[i].value);
    }

    const ppFormOpen = this.hiddenPersonPp[this.lineIndexPp] === false;
    const pmFormOpen = this.hiddenPersonPm[this.lineIndexPm] === false;

    this.localStorageService.save('form_a', {
      pp: ppValues,
      pm: pmValues,
      ppFormOpen,
      pmFormOpen,
      currentPpForm: ppFormOpen ? this.personnePhysiqueData.at(this.lineIndexPp).value : null,
      currentPmForm: pmFormOpen ? this.personneMoraleData.at(this.lineIndexPm).value : null
    });
  }

  /**
   * Restaure les personnes physiques et morales depuis le localStorage.
   * Reconstruit les FormArrays et les tableaux d'affichage.
   */
  private restorePersonsFromStorage(): void {
    const saved = this.localStorageService.load('form_a');
    if (!saved) { return; }

    const savedPp: any[] = saved.pp;
    if (savedPp && savedPp.length > 0) {
      this.resetPersonnePhysique();
      // Remplir la première entrée (déjà créée par resetPersonnePhysique)
      this.personnePhysiqueData.at(0).patchValue(savedPp[0], { emitEvent: false });
      this.hiddenPersonPp[0] = true;
      this.showPersonLinePp[0] = false;

      // Ajouter les personnes suivantes
      for (let i = 1; i < savedPp.length; i++) {
        const group = this.createItem(AppFormAComponent.TYPE_PERSONNE_PHYSIQUE);
        this.personnePhysiqueData.push(group);
        this.personnePhysiqueData.at(i).patchValue(savedPp[i], { emitEvent: false });
        this.hiddenPersonPp[i] = true;
        this.showPersonLinePp[i] = false;
      }

      // Ajouter l'entrée vide pour le prochain ajout
      const emptyPp = this.createItem(AppFormAComponent.TYPE_PERSONNE_PHYSIQUE);
      this.personnePhysiqueData.push(emptyPp);
      this.lineIndexPp = savedPp.length;
      this.hiddenPersonPp[this.lineIndexPp] = true;
      this.showPersonLinePp[this.lineIndexPp] = true;
      this.itemsPp = this.personnePhysiqueData;
    }

    // Restaurer le brouillon du formulaire PP si ouvert
    if (saved.ppFormOpen) {
      this.hiddenPersonPp[this.lineIndexPp] = false;
      if (saved.currentPpForm) {
        this.personnePhysiqueData.at(this.lineIndexPp).patchValue(saved.currentPpForm, { emitEvent: false });
      }
    }

    const savedPm: any[] = saved.pm;
    if (savedPm && savedPm.length > 0) {
      this.resetPersonneMorale();
      this.personneMoraleData.at(0).patchValue(savedPm[0], { emitEvent: false });
      this.hiddenPersonPm[0] = true;
      this.showPersonLinePm[0] = false;

      for (let i = 1; i < savedPm.length; i++) {
        const group = this.createItem(AppFormAComponent.TYPE_PERSONNE_MORALE);
        this.personneMoraleData.push(group);
        this.personneMoraleData.at(i).patchValue(savedPm[i], { emitEvent: false });
        this.hiddenPersonPm[i] = true;
        this.showPersonLinePm[i] = false;
      }

      const emptyPm = this.createItem(AppFormAComponent.TYPE_PERSONNE_MORALE);
      this.personneMoraleData.push(emptyPm);
      this.lineIndexPm = savedPm.length;
      this.hiddenPersonPm[this.lineIndexPm] = true;
      this.showPersonLinePm[this.lineIndexPm] = true;
      this.itemsPm = this.personneMoraleData;
    }

    // Restaurer le brouillon du formulaire PM si ouvert
    if (saved.pmFormOpen) {
      this.hiddenPersonPm[this.lineIndexPm] = false;
      if (saved.currentPmForm) {
        this.personneMoraleData.at(this.lineIndexPm).patchValue(saved.currentPmForm, { emitEvent: false });
      }
    }

    // Marquer les champs restaurés comme dirty pour éviter les faux positifs de validation
    for (let i = 0; i < this.personnePhysiqueData.length; i++) {
      this.markFilledControlsDirty(this.personnePhysiqueData.at(i));
    }
    for (let i = 0; i < this.personneMoraleData.length; i++) {
      this.markFilledControlsDirty(this.personneMoraleData.at(i));
    }
  }

  /**
   * Marque comme dirty tous les contrôles du groupe ayant une valeur non vide.
   * Nécessaire après patchValue pour que checkFieldValidity n'affiche pas
   * d'erreur sur les champs restaurés depuis le localStorage.
   */
  private markFilledControlsDirty(group: any): void {
    if (!group || !group.controls) { return; }
    Object.keys(group.controls).forEach(key => {
      const val = group.controls[key].value;
      if (val !== null && val !== '' && val !== undefined) {
        group.controls[key].markAsDirty();
      }
    });
  }

  /**
   * Comparateur pour la select box profession :
   * compare par code pour éviter les problèmes de référence après désérialisation JSON.
   */
  compareProfession(a: any, b: any): boolean {
    if (!a || !b) { return a === b; }
    return a.code === b.code;
  }

  /**
   * Vérifie si le pays sélectionné est la France.
   * Utilisé dans le template pour conditionner la validation du code postal.
   */
  isFrance(item: any): boolean {
    return item.get('adresse_country').value === 'FR';
  }

  /**
   * Abonne le champ code postal aux changements de pays :
   * en France → pattern 5 chiffres, à l'étranger → pas de contrainte de format.
   */
  private setupCpLogic(group: FormGroup, countryField: string, cpField: string): void {
    group.get(countryField).valueChanges.subscribe(country => {
      const cpControl = group.get(cpField);
      if (country === 'FR') {
        cpControl.setValidators([Validators.required, Validators.pattern(/^\d{5}$/)]);
      } else {
        cpControl.setValidators([Validators.required]);
      }
      cpControl.updateValueAndValidity();
    });
  }

  isButtonsDisabled(): Boolean {
    const length = this.personnePhysiqueData.controls.length + this.personneMoraleData.controls.length;
    // 1 personne max (PP ou PM). Il y a forcément une entrée d'avance dans chaque tableau ;
    // qui est RAZ à l'ajout (pour le formulaire vide), d'où le threshold à une longueur de 3
    if(length > 2) {
      return true;
    } else {
      return null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/app-form-a/app-form-a.component.ts